import { motion } from "framer-motion";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import ScrollIndicator from "@/components/landing/ScrollIndicator";
import heroBgVideo from "@/assets/diagnostic-hero-bg.mp4";

const DiagnosticHero = () => {
  const scrollToCalendly = useCallback(() => {
    const calendlySection = document.getElementById("calendly");
    if (calendlySection) {
      calendlySection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const scrollToTest = () => {
    const testSection = document.getElementById("test");
    if (testSection) {
      testSection.scrollIntoView({
        behavior: "smooth"
      });
    }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-black">
      {/* Background video */}
      <div className="absolute inset-0">
        <video 
          src={heroBgVideo} 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover"
        />
        {/* Blue glow overlay */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: "radial-gradient(ellipse 80% 100% at 0% 50%, rgba(37, 99, 235, 0.4) 0%, rgba(37, 99, 235, 0.15) 30%, transparent 70%)" 
          }} 
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content */}
      <div className="container relative z-10 px-4 py-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="flex flex-col items-center text-center max-w-5xl mx-auto"
        >

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.1] mb-8 text-white tracking-tight"
          >
            Transformamos empresas y líderes
            <br className="hidden md:block" />
            con trayectoria en un activo de{" "}
            <span className="text-gold-light">autoridad y valor</span>.
          </motion.h1>

          {/* Elegant divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="w-16 h-px mb-8 bg-gradient-to-r from-transparent via-gold-light/50 to-transparent"
          />

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="text-lg md:text-xl text-white/80 max-w-3xl mb-6 leading-relaxed font-light"
          >
            Proyectamos el prestigio, elevamos la percepción y blindamos el negocio a través de nuestro método de Capital de marca®.
          </motion.p>

          {/* Supporting line */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="text-base text-white/60 max-w-2xl mb-14 leading-relaxed"
          >
            Llegamos al núcleo para elevar lo esencial.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToCalendly}
              className="tracking-wider font-semibold rounded-full"
            >
              Aplicar Llamada
            </Button>
          </motion.div>

        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
        <ScrollIndicator onClick={scrollToTest} />
      </div>
    </section>
  );
};

export default DiagnosticHero;
